// One-shot signup nudge for first-time anonymous visitors. Fires once
// per browser, ~12s after landing (or when the user shows intent — first
// scroll past the fold). Stores a flag in localStorage so it never
// re-appears on this device.
//
// This is intentionally separate from `FirstOutputSignupGate`, which
// fires AFTER a Hub generation. This one captures purely passive
// visitors who haven't tried anything yet — the "save your work" angle
// gets a head-start on returning visits.
import { useEffect, useState } from "react";
import { Sparkles, X, Trophy, Save, Zap } from "lucide-react";
import AuthModal from "@/components/AuthModal";
import { useAuth } from "@/hooks/useAuth";

const STORAGE_KEY = "hl_first_visit_signup_shown";
const RETURNING_KEY = "hl_returning";
const DELAY_MS = 12_000;

const hasShown = (): boolean => {
  if (typeof window === "undefined") return true;
  try {
    return (
      window.localStorage.getItem(STORAGE_KEY) === "1" ||
      // Skip for returning visitors who've signed in before — pointless
      // to ask them to "save their work" if they've already got an
      // account on this device.
      window.localStorage.getItem(RETURNING_KEY) === "1"
    );
  } catch {
    return true;
  }
};

const markShown = () => {
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* private mode — best effort */
  }
};

const FirstVisitSignupModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    // Only run for genuinely-new anonymous visitors.
    if (user) return;
    if (hasShown()) return;

    let scrollFired = false;
    let timer: number | undefined;

    const trigger = () => {
      if (hasShown()) return;
      if (user) return;
      markShown();
      setOpen(true);
    };

    // Fire on first meaningful scroll (intent signal) or after delay,
    // whichever comes first.
    const onScroll = () => {
      if (scrollFired) return;
      if (window.scrollY > 400) {
        scrollFired = true;
        window.removeEventListener("scroll", onScroll);
        // Slight delay so it doesn't feel instant on scroll.
        window.setTimeout(trigger, 800);
      }
    };

    timer = window.setTimeout(trigger, DELAY_MS);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [user]);

  // If a logged-in user lands here (e.g. signed in mid-session), close.
  useEffect(() => {
    if (user && open) setOpen(false);
  }, [user, open]);

  const handleClose = () => setOpen(false);
  const handleJoin = () => {
    setOpen(false);
    setAuthOpen(true);
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in"
          role="dialog"
          aria-modal="true"
          aria-labelledby="first-visit-title"
        >
          <div className="relative w-full max-w-md rounded-2xl border border-primary/40 bg-card shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-primary/60 via-primary to-primary/60" />

            <button
              type="button"
              onClick={handleClose}
              className="absolute top-3 right-3 w-7 h-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary inline-flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h2
                    id="first-visit-title"
                    className="font-mono font-bold text-base text-foreground"
                  >
                    Save your work
                  </h2>
                  <p className="text-xs font-mono text-muted-foreground">
                    Free account · 10 seconds
                  </p>
                </div>
              </div>

              <p className="text-sm text-foreground/90 leading-relaxed mb-5">
                Sign up free to keep your prompts, points, and unlocked
                tools across every device — and pick up where you left off
                next time you're back.
              </p>

              <div className="grid grid-cols-3 gap-2 mb-5">
                <div className="flex flex-col items-center text-center gap-1.5 p-2.5 rounded-lg border border-border bg-secondary/40">
                  <Save className="w-4 h-4 text-primary" />
                  <span className="text-[10px] font-mono text-muted-foreground leading-tight">
                    Save chats
                  </span>
                </div>
                <div className="flex flex-col items-center text-center gap-1.5 p-2.5 rounded-lg border border-border bg-secondary/40">
                  <Trophy className="w-4 h-4 text-primary" />
                  <span className="text-[10px] font-mono text-muted-foreground leading-tight">
                    Earn points
                  </span>
                </div>
                <div className="flex flex-col items-center text-center gap-1.5 p-2.5 rounded-lg border border-border bg-secondary/40">
                  <Zap className="w-4 h-4 text-primary" />
                  <span className="text-[10px] font-mono text-muted-foreground leading-tight">
                    Unlock tools
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleJoin}
                  className="w-full px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-mono text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  Create free account
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full text-[11px] font-mono text-muted-foreground hover:text-foreground transition-colors"
                >
                  Keep browsing →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultMode="signup"
      />
    </>
  );
};

export default FirstVisitSignupModal;